import React, { FC, memo } from 'react';
import { createTheme, ThemeProvider } from '@material-ui/core/styles';
import { theme } from './theme';
import SvgIcon from '@material-ui/core/SvgIcon';
import { boxShadowForMenu } from './boxShadow';
import { injectGlobal } from '../customEmotion';

const CaretDown = memo(props => (
  <SvgIcon viewBox="0 0 320 512" color="action" {...props}>
    <path d="M31.3 192h257.3c17.8 0 26.7 21.5 14.1 34.1L174.1 354.8c-7.8 7.8-20.5 7.8-28.3 0L17.2 226.1C4.6 213.5 13.5 192 31.3 192z" />
  </SvgIcon>
));

export const muiThemeForSurveyCake = (() => {
  // Side Effects
  injectGlobal`
    @import url('https://fonts.googleapis.com/css?family=Noto+Sans+TC:400,500,700|Roboto:400,500,700&display=swap&subset=chinese-traditional');
  `;

  const BASE_FONT_SIZE = 16;

  return createTheme({
    typography: {
      fontFamily: [
        'Helvetica Neue',
        'Helvetica',
        'Roboto',
        'Arial',
        'Noto Sans TC',
        'Apple LiGothic',
        '微軟正黑體',
        'Microsoft JhengHei',
        'sans-serif',
        'PingFangTC-Regular',
      ].join(','),
      htmlFontSize: BASE_FONT_SIZE,
      h1: {
        fontSize: `${theme.typography.fontSize.h1 / BASE_FONT_SIZE}rem`,
        fontWeight: 500,
        lineHeight: `${44 / BASE_FONT_SIZE}rem`,
      },
      h2: {
        fontSize: `${theme.typography.fontSize.h2 / BASE_FONT_SIZE}rem`,
        fontWeight: 500,
        lineHeight: `${40 / BASE_FONT_SIZE}rem`,
      },
      h3: {
        fontSize: `${theme.typography.fontSize.h3 / BASE_FONT_SIZE}rem`,
        fontWeight: 500,
        lineHeight: `${36 / BASE_FONT_SIZE}rem`,
      },
      h4: {
        fontSize: `${theme.typography.fontSize.h4 / BASE_FONT_SIZE}rem`,
        fontWeight: 500,
        lineHeight: `${32 / BASE_FONT_SIZE}rem`,
      },
      h5: {
        fontSize: `${theme.typography.fontSize.h5 / BASE_FONT_SIZE}rem`,
        fontWeight: 500,
        lineHeight: `${28 / BASE_FONT_SIZE}rem`,
      },
      h6: {
        fontSize: `${theme.typography.fontSize.h6 / BASE_FONT_SIZE}rem`,
        fontWeight: 500,
        lineHeight: `${24 / BASE_FONT_SIZE}rem`,
      },
      body1: {
        fontFamily: 'PingFangTC-Regular',
        fontSize: `${theme.typography.fontSize.p / BASE_FONT_SIZE}rem`,
        lineHeight: `${22 / BASE_FONT_SIZE}rem`,
      },
      body2: {
        fontSize: `${12 / BASE_FONT_SIZE}rem`,
        lineHeight: `${20 / BASE_FONT_SIZE}rem`,
      },
      button: {
        fontSize: `${12 / BASE_FONT_SIZE}rem`,
        lineHeight: `${20 / BASE_FONT_SIZE}rem`,
      },
      caption: {
        fontSize: `${12 / BASE_FONT_SIZE}rem`,
        lineHeight: `${20 / BASE_FONT_SIZE}rem`,
      },
      overline: {
        fontSize: `${12 / BASE_FONT_SIZE}rem`,
        lineHeight: `${20 / BASE_FONT_SIZE}rem`,
      },
    },
    palette: {
      primary: {
        light: theme.color.primary.hover,
        main: theme.color.primary.default,
        dark: theme.color.primary.active,
        contrastText: '#ffffff',
      },
      secondary: {
        light: theme.color.secondary.hover,
        main: theme.color.secondary.default,
        dark: theme.color.secondary.active,
        contrastText: '#ffffff',
      },
      action: {
        hoverOpacity: 0.25,
      },
      error: {
        main: theme.color.semantic.error,
      },
    },
    props: {
      // Name of the component ⚛️
      MuiButtonBase: {
        // The default props to change
        disableRipple: true, // No more ripple, on the whole application 💣!
      },
      MuiSelect: {
        MenuProps: {
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'center',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'center',
          },
          getContentAnchorEl: null,
        },
        IconComponent: CaretDown,
      },
    },
    overrides: {
      MuiPopover: {
        paper: {
          marginTop: '8px',
        },
      },
      // Style sheet name ⚛️
      MuiButton: {
        root: {
          minWidth: 'none',
          padding: '7px 20px',
          color: theme.color.text.B3,
          fontWeight: 400,
          fontSize: `${14 / BASE_FONT_SIZE}rem`,
          lineHeight: `${22 / BASE_FONT_SIZE}rem`,
          '&:hover': {
            backgroundColor: theme.color.text.HB,
          },
          '&:disabled': {
            color: theme.color.text.B2,
            backgroundColor: theme.color.text.F,
          },
        },
        // Name of the rule
        contained: {
          // Some CSS
          backgroundColor: theme.color.text.HB,
          color: theme.color.text.B4,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
            backgroundColor: theme.color.text.HB,
            color: theme.color.text.B5,
          },
          '&:active': {
            boxShadow: 'none',
          },
          '&$focusVisible': {
            boxShadow: 'none',
          },
        },
        containedPrimary: {
          '&:disabled': {
            color: theme.color.text.white,
          },
          '&:hover': {
            boxShadow: 'none',
            color: theme.color.text.white,
          },
        },
        containedSecondary: {
          '&:disabled': {
            color: theme.color.text.white,
          },
          '&:hover': {
            boxShadow: 'none',
            color: theme.color.text.white,
          },
        },
        containedSizeSmall: {
          padding: '5px 16px',
          fontSize: `${14 / BASE_FONT_SIZE}rem`,
          lineHeight: `${22 / BASE_FONT_SIZE}rem`,
        },
        containedSizeLarge: {
          padding: '8px 24px',
          fontSize: `${16 / BASE_FONT_SIZE}rem`,
          lineHeight: `${24 / BASE_FONT_SIZE}rem`,
        },
        outlined: {
          padding: '7px 20px',
        },
        outlinedPrimary: {
          borderColor: theme.color.primary.default,
        },
        outlinedSizeSmall: {
          padding: '5px 16px',
          fontSize: `${14 / BASE_FONT_SIZE}rem`,
          lineHeight: `${22 / BASE_FONT_SIZE}rem`,
        },
        outlinedSizeLarge: {
          padding: '8px 24px',
          fontSize: `${16 / BASE_FONT_SIZE}rem`,
          lineHeight: `${24 / BASE_FONT_SIZE}rem`,
        },
      },
      MuiChip: {
        root: {
          borderRadius: 4,
          '& span': {
            padding: '0 8px',
          },
        },
      },
      MuiMenu: {
        paper: {
          boxShadow: boxShadowForMenu,
        },
      },
      MuiMenuItem: {
        root: {
          minHeight: 'auto',
        },
      },
      MuiList: {
        root: {
          zIndex: 100,
        },
        padding: {
          padding: '8px',
        },
      },
      MuiRadio: {
        root: {
          padding: 5,
        },
        colorSecondary: {
          '&:hover': {
            backgroundColor: 'transparent',
          },
          MuiChecked: {
            '&:hover': {
              backgroundColor: 'transparent',
            },
          },
        },
      },
      MuiListItem: {
        root: {
          borderRadius: '0',
          '&$selected': {
            backgroundColor: theme.color.primary.lightBackground,
            color: theme.color.primary.default,
            '&:hover': {
              backgroundColor: theme.color.primary.lightBackground,
            },
          },
        },
        button: {
          '&:hover': {
            backgroundColor: 'white',
            color: theme.color.primary.default,
          },
        },
      },
      MuiTable: {
        root: {
          backgroundColor: 'white',
        },
      },
      MuiTableRow: {
        root: {
          '&$hover': {
            '&:hover': {
              backgroundColor: '#fafafa',
            },
          },
        },
      },
      MuiSelect: {
        selectMenu: {
          '&&': {
            paddingRight: '32px',
            color: '#aaa',
          },
        },
        select: {
          '&:focus': {
            backgroundColor: 'white',
          },
        },
        icon: {
          color: '#aaa',
        },
      },
      MuiOutlinedInput: {
        root: {
          '&:hover $notchedOutline': {
            borderColor: theme.color.primary.default,
          },
          '&$focused $notchedOutline': {
            borderWidth: '1px',
            boxShadow: `0 0 2px 0 ${theme.color.primary.default}`,
          },
        },
        input: {
          padding: '8px',
        },
        notchedOutline: {
          borderColor: '#eee',
        },
        adornedStart: {
          paddingLeft: '12px',
        },
      },
      MuiInputBase: {
        root: {
          fontSize: `${14 / BASE_FONT_SIZE}rem`,
        },
      },
      MuiIconButton: {
        root: {
          '&:hover': {
            '& *': {
              color: 'inherit',
            },
          },
        },
      },
      MuiTooltip: {
        tooltip: {
          backgroundColor: theme.color.text.B6,
          fontSize: theme.typography.fontSize.span,
        },
        arrow: {
          color: theme.color.text.B6,
        },
      },
      MuiCardHeader: {
        root: {
          padding: '13px 24px',
          backgroundColor: theme.color.primary.default,
        },
      },
      MuiCardContent: {
        root: {
          padding: '13px 24px',
        },
      },
      MuiCardActions: {
        root: {
          padding: '13px 24px',
          backgroundColor: '#fafafa',
          borderTop: `1px solid ${theme.color.text.HB}`,
        },
      },
      MuiSnackbarContent: {
        root: {
          borderRadius: '4px',
          boxShadow:
            '0 1px 8px 0 rgba(69, 69, 69, 0.2), 0 1px 6px 0 rgba(69, 69, 69, 0.1)',
        },
      },
      MuiTabs: {
        indicator: {
          backgroundColor: theme.color.primary.default,
        },
      },
    },
  });
})();

export const MuiThemeProvider: FC = ({ children }) => (
  <ThemeProvider theme={muiThemeForSurveyCake}>{children}</ThemeProvider>
);
